import { useEffect, useMemo, useRef, useState } from 'react';
import PhoneMockup from './PhoneMockup';

// Guion de la demo: todos los mensajes quedan tipeados letra por letra en la barra de
// abajo, igual a como responde hoy el bot real (horarios libres escritos en texto).
const SCRIPT = [
  { from: 'customer', text: 'Hola! ¿Tienen espacio para manicure gel este sábado?' },
  {
    from: 'bot',
    text: '¡Hola! 💅 El sábado tengo libre:\n• 10:00 a. m. con Carla\n• 11:30 a. m. con Ana\n• 4:00 p. m. con Carla\n\nLa manicure gel cuesta S/45 y dura 1 hora. ¿Cuál te separo?'
  },
  { from: 'customer', text: 'La de 11:30 con Ana' },
  {
    from: 'bot',
    text: '✅ ¡Listo, Lucía! Te separé:\n📅 Sábado 11:30 a. m.\n💅 Manicure gel con Ana\n\nUn día antes te escribo para confirmar 😊',
    notifyAppointment: { name: 'Lucía', when: 'Sáb 11:30', professional: 'Ana' }
  }
];

const TIMING = {
  typing: 1000,
  typeChar: 45,
  pauseAfterBot: 1500,
  pauseAfterCustomer: 800,
  pauseAtEnd: 3200,
  pauseBeforeRestart: 500,
  checkmarkDelay: 600,
  notificationDelay: 500,
  notificationHold: 4200
};

// Patrón de fondo propio: doodles sutiles de belleza (esmalte, flor, tijeras, corazón)
// sobre beige claro, como SVG embebido (sin assets externos).
const CHAT_BG_SVG = `
<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'>
  <rect width='140' height='140' fill='#f2e9da'/>
  <g fill='none' stroke='#c9b89a' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round' opacity='0.55'>
    <rect x='14' y='18' width='11' height='16' rx='2.5'/>
    <rect x='16' y='12' width='7' height='7' rx='1.5'/>
    <circle cx='95' cy='22' r='3.2'/>
    <circle cx='87' cy='22' r='3.2'/>
    <circle cx='103' cy='22' r='3.2'/>
    <circle cx='95' cy='14' r='3.2'/>
    <circle cx='95' cy='30' r='3.2'/>
    <circle cx='95' cy='22' r='1.6' fill='#c9b89a' stroke='none'/>
    <circle cx='24' cy='90' r='3.4'/>
    <circle cx='24' cy='101' r='3.4'/>
    <line x1='27' y1='92' x2='44' y2='108'/>
    <line x1='27' y1='99' x2='44' y2='83'/>
    <path d='M112 90c-3.2-4-9.6-1.8-9.6 2.8 0 5.4 9.6 10.2 9.6 10.2s9.6-4.8 9.6-10.2c0-4.6-6.4-6.8-9.6-2.8z'/>
  </g>
</svg>`.trim();
const CHAT_BG_URL = `url("data:image/svg+xml,${encodeURIComponent(CHAT_BG_SVG)}")`;

function usePrefersReducedMotion() {
  return useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);
}

function DoubleCheck({ read }) {
  return (
    <svg
      className={`inline-block h-3.5 w-3.5 -mb-px transition-colors duration-300 motion-reduce:transition-none ${
        read ? 'text-sky-500' : 'text-gray-400'
      }`}
      viewBox="0 0 18 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 6.5L4.5 10L11 2" />
      <path d="M6.5 6.5L10 10L16.5 2" />
    </svg>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start message-in">
      <div className="bg-white rounded-2xl rounded-tl-md px-3 py-3 shadow-sm flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce motion-reduce:animate-none" style={{ animationDelay: '0ms' }} />
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce motion-reduce:animate-none" style={{ animationDelay: '150ms' }} />
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce motion-reduce:animate-none" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}

function ChatDemo() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const speed = prefersReducedMotion ? 0.15 : 1;

  const [visibleCount, setVisibleCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [readIndexes, setReadIndexes] = useState(() => new Set());
  const [notification, setNotification] = useState(null);
  const [notificationShown, setNotificationShown] = useState(false);
  const readTimersRef = useRef([]);

  useEffect(() => {
    let mounted = true;
    let timeoutId;
    let count = 0;

    const wait = (ms) =>
      new Promise((resolve) => {
        timeoutId = setTimeout(resolve, Math.max(ms * speed, 10));
      });

    async function run() {
      while (mounted) {
        if (count >= SCRIPT.length) {
          await wait(TIMING.pauseAtEnd);
          if (!mounted) return;
          readTimersRef.current.forEach(clearTimeout);
          readTimersRef.current = [];
          count = 0;
          setVisibleCount(0);
          setIsTyping(false);
          setDraftText('');
          setReadIndexes(new Set());
          setNotificationShown(false);
          setNotification(null);
          await wait(TIMING.pauseBeforeRestart);
          continue;
        }

        const step = SCRIPT[count];

        if (step.from === 'bot') {
          setIsTyping(true);
          await wait(TIMING.typing);
          if (!mounted) return;
          setIsTyping(false);
          count += 1;
          setVisibleCount(count);

          if (step.notifyAppointment) {
            await wait(TIMING.notificationDelay);
            if (!mounted) return;
            setNotification(step.notifyAppointment);
            requestAnimationFrame(() => setNotificationShown(true));
          }

          await wait(TIMING.pauseAfterBot);
        } else {
          for (let c = 1; c <= step.text.length; c++) {
            setDraftText(step.text.slice(0, c));
            // eslint-disable-next-line no-await-in-loop
            await wait(TIMING.typeChar);
            if (!mounted) return;
          }
          await wait(200);
          setDraftText('');

          count += 1;
          setVisibleCount(count);
          const sentIndex = count - 1;
          const t = setTimeout(() => {
            setReadIndexes((prev) => new Set(prev).add(sentIndex));
          }, TIMING.checkmarkDelay * speed);
          readTimersRef.current.push(t);

          await wait(TIMING.pauseAfterCustomer);
        }
      }
    }

    run();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      readTimersRef.current.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleMessages = SCRIPT.slice(0, visibleCount);

  return (
    <div
      className="relative w-full max-w-sm mx-auto"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, Roboto, system-ui, sans-serif' }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-green-300 to-lime-300 blur-3xl opacity-30 scale-90" />

      <div className="relative">
        <PhoneMockup>
          {/* Encabezado de la conversación */}
          <div className="bg-green-600 px-2 py-2.5 flex items-center gap-2">
            <svg className="h-5 w-5 text-white shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <div className="h-9 w-9 rounded-full bg-rose-100 flex items-center justify-center text-rose-700 shrink-0 text-xs font-bold tracking-tight">
              BS
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold leading-tight truncate">Bella Spa</p>
              <p className="text-green-100 text-[11px] leading-tight">
                {isTyping ? 'escribiendo…' : 'Cuenta de empresa'}
              </p>
            </div>
            <svg className="h-4 w-4 text-white shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z" />
            </svg>
            <svg className="h-4 w-4 text-white shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.6 10.8c1.4 2.7 3.6 4.9 6.3 6.3l2.1-2.1c.3-.3.7-.4 1-.2 1.1.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.6c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2 2z" />
            </svg>
          </div>

          {/* Área de mensajes — altura fija, sin saltos de layout */}
          <div
            className="h-[26rem] px-3 py-4 space-y-1 flex flex-col justify-end overflow-hidden"
            style={{
              backgroundColor: '#f2e9da',
              backgroundImage: CHAT_BG_URL,
              backgroundSize: '140px 140px'
            }}
          >
            <div className="flex justify-center mb-1">
              <span className="bg-white/85 text-gray-500 text-[10px] font-medium tracking-wide px-2.5 py-1 rounded-lg shadow-sm">
                HOY
              </span>
            </div>

            {visibleMessages.map((msg, i) => {
              const isCustomer = msg.from === 'customer';
              const isFirstInGroup = i === 0 || visibleMessages[i - 1].from !== msg.from;
              const time = '11:02 p. m.';

              return (
                <div key={i} className={`flex message-in ${isCustomer ? 'justify-end' : 'justify-start'} ${isFirstInGroup ? 'mt-2' : ''}`}>
                  <div className="max-w-[80%]">
                    <div
                      className={`relative px-3 py-2 text-[13.5px] leading-snug whitespace-pre-line shadow-sm rounded-2xl ${
                        isCustomer
                          ? `bg-[#dcf8c6] text-gray-900 ${isFirstInGroup ? 'tail-right rounded-tr-md' : ''}`
                          : `bg-white text-gray-900 ${isFirstInGroup ? 'tail-left rounded-tl-md' : ''}`
                      }`}
                    >
                      {msg.text}
                      <span className="float-right ml-2 mt-1 flex items-center gap-1 text-[10px] text-gray-400 select-none">
                        {time}
                        {isCustomer && <DoubleCheck read={readIndexes.has(i)} />}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {isTyping && <TypingBubble />}
          </div>

          {/* Barra de entrada */}
          <div className="bg-[#f0f0f0] px-2.5 py-2 flex items-center gap-2">
            <div className="flex-1 bg-white rounded-full px-3 py-1.5 text-xs text-gray-700 min-h-[1.75rem] flex items-center">
              {draftText ? (
                <span>
                  {draftText}
                  <span className="inline-block w-px h-3 bg-gray-400 ml-0.5 align-middle animate-pulse motion-reduce:animate-none" />
                </span>
              ) : (
                <span className="text-gray-400">Mensaje</span>
              )}
            </div>
            <div className="h-8 w-8 rounded-full bg-green-600 flex items-center justify-center shrink-0">
              <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
              </svg>
            </div>
          </div>
        </PhoneMockup>

        {/* Notificación flotante del panel: siempre debajo del teléfono, con altura
            reservada desde el inicio para que no haya saltos de layout cuando aparece. */}
        <div className="mt-4 w-full max-w-[19rem] mx-auto min-h-[4.5rem]">
          <div
            className={`bg-white rounded-xl shadow-lg border border-gray-100 px-3.5 py-3 transition-all duration-500 motion-reduce:transition-none ${
              notificationShown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
            }`}
          >
            <p className="text-[11px] font-semibold text-gray-900">📅 Nueva cita</p>
            {notification && (
              <p className="text-[11px] text-gray-500 mt-0.5">
                {notification.name} · {notification.when} · {notification.professional}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mt-4">
        {SCRIPT.map((_, i) => (
          <span
            key={i}
            className={`h-2 rounded-full transition-all motion-reduce:transition-none ${
              i + 1 === visibleCount ? 'w-6 bg-green-600' : 'w-2 bg-gray-300'
            }`}
          />
        ))}
      </div>

      <style>{`
        @keyframes messageIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .message-in {
          animation: messageIn 0.25s ease-out;
        }
        @media (prefers-reduced-motion: reduce) {
          .message-in { animation: none; }
        }
        .tail-left::before {
          content: '';
          position: absolute;
          top: 0;
          left: -6px;
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 0 7px 9px 0;
          border-color: transparent #ffffff transparent transparent;
        }
        .tail-right::before {
          content: '';
          position: absolute;
          top: 0;
          right: -6px;
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 0 0 9px 7px;
          border-color: transparent transparent transparent #dcf8c6;
        }
      `}</style>
    </div>
  );
}

export default ChatDemo;
